// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.11.2
//   protoc               v5.26.1
// source: atk/effective_config.proto

/* eslint-disable */
import { BinaryReader, BinaryWriter } from "@bufbuild/protobuf/wire";
import { Polygon2D } from "../geometry/geometry";
import { Timestamp } from "../google/protobuf/timestamp";
import {
  AutotractorSettings,
  ImplementControlSchema,
  ImplementHeightTurningRadiusLimit,
  ScvInput,
  ScvOutput,
  SteeringRange,
  TractorHitch,
} from "./autotractor";
import { ImplementCalibration, TractorCalibration } from "./calibration";
import {
  AtimInterface,
  atimInterfaceFromJSON,
  atimInterfaceToJSON,
  DataModelVersion,
  dataModelVersionFromJSON,
  dataModelVersionToJSON,
  DriveType,
  driveTypeFromJSON,
  driveTypeToJSON,
  PtoSpeed,
  ptoSpeedFromJSON,
  ptoSpeedToJSON,
  PtoType,
  ptoTypeFromJSON,
  ptoTypeToJSON,
  TurnDirection,
  turnDirectionFromJSON,
  turnDirectionToJSON,
} from "./enums";

export const protobufPackage = "carbon.atk";

export enum EffectiveConfigValidationMode {
  VALIDATION_MODE_UNSPECIFIED = 0,
  /**
   * VALIDATION_MODE_FULL - the default validation mode: the entire rig configuration is validated,
   * both the tractor + implement themselves plus the entire rig configuration.
   * this validation mode is always used for taking rig snapshots.
   */
  VALIDATION_MODE_FULL = 1,
  /**
   * VALIDATION_MODE_NO_ACTIVITIES - instead of being read normally, Autotractor.controls.activities is treated
   * as if it is empty; this allows the action builder UI to rely on
   * normalization logic done in the validation process, while still preserving
   * an escape hatch for the user to fix validation errors they create in the
   * action configuration by making changes to the underlying hardware.
   */
  VALIDATION_MODE_NO_ACTIVITIES = 2,
  UNRECOGNIZED = -1,
}

export function effectiveConfigValidationModeFromJSON(object: any): EffectiveConfigValidationMode {
  switch (object) {
    case 0:
    case "VALIDATION_MODE_UNSPECIFIED":
      return EffectiveConfigValidationMode.VALIDATION_MODE_UNSPECIFIED;
    case 1:
    case "VALIDATION_MODE_FULL":
      return EffectiveConfigValidationMode.VALIDATION_MODE_FULL;
    case 2:
    case "VALIDATION_MODE_NO_ACTIVITIES":
      return EffectiveConfigValidationMode.VALIDATION_MODE_NO_ACTIVITIES;
    case -1:
    case "UNRECOGNIZED":
    default:
      return EffectiveConfigValidationMode.UNRECOGNIZED;
  }
}

export function effectiveConfigValidationModeToJSON(object: EffectiveConfigValidationMode): string {
  switch (object) {
    case EffectiveConfigValidationMode.VALIDATION_MODE_UNSPECIFIED:
      return "VALIDATION_MODE_UNSPECIFIED";
    case EffectiveConfigValidationMode.VALIDATION_MODE_FULL:
      return "VALIDATION_MODE_FULL";
    case EffectiveConfigValidationMode.VALIDATION_MODE_NO_ACTIVITIES:
      return "VALIDATION_MODE_NO_ACTIVITIES";
    case EffectiveConfigValidationMode.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * the messages in this file represent the "effective config" of a particular
 * Autotractor rig -- the values after walking the equipment/archetype tree and
 * resolving all of the cross-references and overrides.
 *
 * this exists so that we can have this calculation live in portal and show
 * definitive values to the user without having to keep calculations synced
 * between portal, jobs, and the robot.
 */
export interface RigEffectiveConfig {
  rigId: string;
  dataModelVersion: DataModelVersion;
  snapshotAt: string | undefined;
  autotractorSettings: AutotractorSettings | undefined;
  tractor: TractorEffectiveConfig | undefined;
  implements: ImplementEffectiveConfig[];
  /**
   * PTO config, combined from tractor.{front,rear}_pto and
   * implement.pto_requirements
   */
  ptos: PtoEffectiveConfig[];
  /**
   * SCV config, combined from autotractor.scv_connections, tractor.scv_outputs,
   * and implement.{scv_inputs,scv_control_presets}
   */
  scvs: ScvChannelEffectiveConfig[];
  controls: ImplementControlSchema | undefined;
}

export interface PtoEffectiveConfig {
  implementId: string;
  ptoType: PtoType;
  engineSpeedRpm: number;
  ptoSpeed: PtoSpeed;
}

/**
 * the effective configuration of an SCV "channel".  usually consists of an
 * output/input/connection triple.  for the use case of a hydraulic toplink,
 * implement_id = scv_connection_id = input = nil.
 */
export interface ScvChannelEffectiveConfig {
  implementId?: string | undefined;
  scvConnectionId?: string | undefined;
  input?: ScvInput | undefined;
  output: ScvOutput | undefined;
}

export interface TractorEffectiveConfig {
  tractorId: string;
  dataModelVersion: DataModelVersion;
  snapshotAt: string | undefined;
  make: string;
  model: string;
  series?: string | undefined;
  customerProvidedName: string;
  steeringRange:
    | SteeringRange
    | undefined;
  /** @deprecated */
  minTurningRadiusM: number;
  wheelSpacingM: number;
  wheelbaseM: number;
  driveType: DriveType;
  boundary: Polygon2D | undefined;
  tractorArchetypeId: string;
  calibration?: TractorCalibration | undefined;
}

export interface ImplementEffectiveConfig {
  implementId: string;
  dataModelVersion: DataModelVersion;
  implementArchetypeId?: string | undefined;
  snapshotAt: string | undefined;
  make: string;
  model: string;
  series?: string | undefined;
  customerProvidedName: string;
  activeHitch: TractorHitch | undefined;
  atim?:
    | AtimInterface
    | undefined;
  /**
   * working_width_m is deprecated in favor of working_width_m_left/right
   * because it does not account for implements which are not symmetrical about
   * the hitch point.
   *
   * @deprecated
   */
  workingWidthM: number;
  /**
   * working_width_m_left is the width of the implement's worked swath left of
   * the hitch point.
   */
  workingWidthMLeft: number;
  /**
   * working_width_m_right is the width of the implement's worked swath right of
   * the hitch point.
   */
  workingWidthMRight: number;
  /**
   * working_start_offset_m is the distance between the hitch point and the
   * front plane of the working area of the implement
   */
  workingStartOffsetM: number;
  /**
   * working_end_offset_m is the distance between the hitch point and the end
   * plane of the working area of the implement
   */
  workingEndOffsetM: number;
  /**
   * turn_direction is the only direction the implement can turn while
   * lowered; unset when it can turn either way.
   */
  turnDirection?:
    | TurnDirection
    | undefined;
  /** @deprecated */
  toolSpeedMS: number;
  /** @deprecated */
  transitionSpeedMS: number;
  /** @deprecated */
  runUpDistanceM: number;
  /** @deprecated */
  straightenDistanceM: number;
  /** @deprecated */
  turningRadiusLimits: ImplementHeightTurningRadiusLimit[];
  boundary: Polygon2D | undefined;
  calibration?: ImplementCalibration | undefined;
}

function createBaseRigEffectiveConfig(): RigEffectiveConfig {
  return {
    rigId: "",
    dataModelVersion: 0,
    snapshotAt: undefined,
    autotractorSettings: undefined,
    tractor: undefined,
    implements: [],
    ptos: [],
    scvs: [],
    controls: undefined,
  };
}

export const RigEffectiveConfig: MessageFns<RigEffectiveConfig> = {
  encode(message: RigEffectiveConfig, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.rigId !== "") {
      writer.uint32(10).string(message.rigId);
    }
    if (message.dataModelVersion !== 0) {
      writer.uint32(72).int32(message.dataModelVersion);
    }
    if (message.snapshotAt !== undefined) {
      Timestamp.encode(toTimestamp(message.snapshotAt), writer.uint32(18).fork()).join();
    }
    if (message.autotractorSettings !== undefined) {
      AutotractorSettings.encode(message.autotractorSettings, writer.uint32(26).fork()).join();
    }
    if (message.tractor !== undefined) {
      TractorEffectiveConfig.encode(message.tractor, writer.uint32(34).fork()).join();
    }
    for (const v of message.implements) {
      ImplementEffectiveConfig.encode(v!, writer.uint32(42).fork()).join();
    }
    for (const v of message.ptos) {
      PtoEffectiveConfig.encode(v!, writer.uint32(50).fork()).join();
    }
    for (const v of message.scvs) {
      ScvChannelEffectiveConfig.encode(v!, writer.uint32(58).fork()).join();
    }
    if (message.controls !== undefined) {
      ImplementControlSchema.encode(message.controls, writer.uint32(66).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): RigEffectiveConfig {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRigEffectiveConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.rigId = reader.string();
          continue;
        }
        case 9: {
          if (tag !== 72) {
            break;
          }

          message.dataModelVersion = reader.int32() as any;
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.snapshotAt = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.autotractorSettings = AutotractorSettings.decode(reader, reader.uint32());
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.tractor = TractorEffectiveConfig.decode(reader, reader.uint32());
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.implements.push(ImplementEffectiveConfig.decode(reader, reader.uint32()));
          continue;
        }
        case 6: {
          if (tag !== 50) {
            break;
          }

          message.ptos.push(PtoEffectiveConfig.decode(reader, reader.uint32()));
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.scvs.push(ScvChannelEffectiveConfig.decode(reader, reader.uint32()));
          continue;
        }
        case 8: {
          if (tag !== 66) {
            break;
          }

          message.controls = ImplementControlSchema.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RigEffectiveConfig {
    return {
      rigId: isSet(object.rigId)
        ? globalThis.String(object.rigId)
        : isSet(object.rig_id)
        ? globalThis.String(object.rig_id)
        : "",
      dataModelVersion: isSet(object.dataModelVersion)
        ? dataModelVersionFromJSON(object.dataModelVersion)
        : isSet(object.data_model_version)
        ? dataModelVersionFromJSON(object.data_model_version)
        : 0,
      snapshotAt: isSet(object.snapshotAt)
        ? globalThis.String(object.snapshotAt)
        : isSet(object.snapshot_at)
        ? globalThis.String(object.snapshot_at)
        : undefined,
      autotractorSettings: isSet(object.autotractorSettings)
        ? AutotractorSettings.fromJSON(object.autotractorSettings)
        : isSet(object.autotractor_settings)
        ? AutotractorSettings.fromJSON(object.autotractor_settings)
        : undefined,
      tractor: isSet(object.tractor) ? TractorEffectiveConfig.fromJSON(object.tractor) : undefined,
      implements: globalThis.Array.isArray(object?.implements)
        ? object.implements.map((e: any) => ImplementEffectiveConfig.fromJSON(e))
        : [],
      ptos: globalThis.Array.isArray(object?.ptos) ? object.ptos.map((e: any) => PtoEffectiveConfig.fromJSON(e)) : [],
      scvs: globalThis.Array.isArray(object?.scvs)
        ? object.scvs.map((e: any) => ScvChannelEffectiveConfig.fromJSON(e))
        : [],
      controls: isSet(object.controls) ? ImplementControlSchema.fromJSON(object.controls) : undefined,
    };
  },

  toJSON(message: RigEffectiveConfig): unknown {
    const obj: any = {};
    if (message.rigId !== "") {
      obj.rigId = message.rigId;
    }
    if (message.dataModelVersion !== 0) {
      obj.dataModelVersion = dataModelVersionToJSON(message.dataModelVersion);
    }
    if (message.snapshotAt !== undefined) {
      obj.snapshotAt = message.snapshotAt;
    }
    if (message.autotractorSettings !== undefined) {
      obj.autotractorSettings = AutotractorSettings.toJSON(message.autotractorSettings);
    }
    if (message.tractor !== undefined) {
      obj.tractor = TractorEffectiveConfig.toJSON(message.tractor);
    }
    if (message.implements?.length) {
      obj.implements = message.implements.map((e) => ImplementEffectiveConfig.toJSON(e));
    }
    if (message.ptos?.length) {
      obj.ptos = message.ptos.map((e) => PtoEffectiveConfig.toJSON(e));
    }
    if (message.scvs?.length) {
      obj.scvs = message.scvs.map((e) => ScvChannelEffectiveConfig.toJSON(e));
    }
    if (message.controls !== undefined) {
      obj.controls = ImplementControlSchema.toJSON(message.controls);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RigEffectiveConfig>, I>>(base?: I): RigEffectiveConfig {
    return RigEffectiveConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RigEffectiveConfig>, I>>(object: I): RigEffectiveConfig {
    const message = createBaseRigEffectiveConfig();
    message.rigId = object.rigId ?? "";
    message.dataModelVersion = object.dataModelVersion ?? 0;
    message.snapshotAt = object.snapshotAt ?? undefined;
    message.autotractorSettings = (object.autotractorSettings !== undefined && object.autotractorSettings !== null)
      ? AutotractorSettings.fromPartial(object.autotractorSettings)
      : undefined;
    message.tractor = (object.tractor !== undefined && object.tractor !== null)
      ? TractorEffectiveConfig.fromPartial(object.tractor)
      : undefined;
    message.implements = object.implements?.map((e) => ImplementEffectiveConfig.fromPartial(e)) || [];
    message.ptos = object.ptos?.map((e) => PtoEffectiveConfig.fromPartial(e)) || [];
    message.scvs = object.scvs?.map((e) => ScvChannelEffectiveConfig.fromPartial(e)) || [];
    message.controls = (object.controls !== undefined && object.controls !== null)
      ? ImplementControlSchema.fromPartial(object.controls)
      : undefined;
    return message;
  },
};

function createBasePtoEffectiveConfig(): PtoEffectiveConfig {
  return { implementId: "", ptoType: 0, engineSpeedRpm: 0, ptoSpeed: 0 };
}

export const PtoEffectiveConfig: MessageFns<PtoEffectiveConfig> = {
  encode(message: PtoEffectiveConfig, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.implementId !== "") {
      writer.uint32(10).string(message.implementId);
    }
    if (message.ptoType !== 0) {
      writer.uint32(16).int32(message.ptoType);
    }
    if (message.engineSpeedRpm !== 0) {
      writer.uint32(24).int32(message.engineSpeedRpm);
    }
    if (message.ptoSpeed !== 0) {
      writer.uint32(32).int32(message.ptoSpeed);
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): PtoEffectiveConfig {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePtoEffectiveConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.implementId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 16) {
            break;
          }

          message.ptoType = reader.int32() as any;
          continue;
        }
        case 3: {
          if (tag !== 24) {
            break;
          }

          message.engineSpeedRpm = reader.int32();
          continue;
        }
        case 4: {
          if (tag !== 32) {
            break;
          }

          message.ptoSpeed = reader.int32() as any;
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PtoEffectiveConfig {
    return {
      implementId: isSet(object.implementId)
        ? globalThis.String(object.implementId)
        : isSet(object.implement_id)
        ? globalThis.String(object.implement_id)
        : "",
      ptoType: isSet(object.ptoType)
        ? ptoTypeFromJSON(object.ptoType)
        : isSet(object.pto_type)
        ? ptoTypeFromJSON(object.pto_type)
        : 0,
      engineSpeedRpm: isSet(object.engineSpeedRpm)
        ? globalThis.Number(object.engineSpeedRpm)
        : isSet(object.engine_speed_rpm)
        ? globalThis.Number(object.engine_speed_rpm)
        : 0,
      ptoSpeed: isSet(object.ptoSpeed)
        ? ptoSpeedFromJSON(object.ptoSpeed)
        : isSet(object.pto_speed)
        ? ptoSpeedFromJSON(object.pto_speed)
        : 0,
    };
  },

  toJSON(message: PtoEffectiveConfig): unknown {
    const obj: any = {};
    if (message.implementId !== "") {
      obj.implementId = message.implementId;
    }
    if (message.ptoType !== 0) {
      obj.ptoType = ptoTypeToJSON(message.ptoType);
    }
    if (message.engineSpeedRpm !== 0) {
      obj.engineSpeedRpm = Math.round(message.engineSpeedRpm);
    }
    if (message.ptoSpeed !== 0) {
      obj.ptoSpeed = ptoSpeedToJSON(message.ptoSpeed);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PtoEffectiveConfig>, I>>(base?: I): PtoEffectiveConfig {
    return PtoEffectiveConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PtoEffectiveConfig>, I>>(object: I): PtoEffectiveConfig {
    const message = createBasePtoEffectiveConfig();
    message.implementId = object.implementId ?? "";
    message.ptoType = object.ptoType ?? 0;
    message.engineSpeedRpm = object.engineSpeedRpm ?? 0;
    message.ptoSpeed = object.ptoSpeed ?? 0;
    return message;
  },
};

function createBaseScvChannelEffectiveConfig(): ScvChannelEffectiveConfig {
  return { implementId: undefined, scvConnectionId: undefined, input: undefined, output: undefined };
}

export const ScvChannelEffectiveConfig: MessageFns<ScvChannelEffectiveConfig> = {
  encode(message: ScvChannelEffectiveConfig, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.implementId !== undefined) {
      writer.uint32(10).string(message.implementId);
    }
    if (message.scvConnectionId !== undefined) {
      writer.uint32(42).string(message.scvConnectionId);
    }
    if (message.input !== undefined) {
      ScvInput.encode(message.input, writer.uint32(18).fork()).join();
    }
    if (message.output !== undefined) {
      ScvOutput.encode(message.output, writer.uint32(26).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ScvChannelEffectiveConfig {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseScvChannelEffectiveConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.implementId = reader.string();
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.scvConnectionId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.input = ScvInput.decode(reader, reader.uint32());
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.output = ScvOutput.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ScvChannelEffectiveConfig {
    return {
      implementId: isSet(object.implementId)
        ? globalThis.String(object.implementId)
        : isSet(object.implement_id)
        ? globalThis.String(object.implement_id)
        : undefined,
      scvConnectionId: isSet(object.scvConnectionId)
        ? globalThis.String(object.scvConnectionId)
        : isSet(object.scv_connection_id)
        ? globalThis.String(object.scv_connection_id)
        : undefined,
      input: isSet(object.input) ? ScvInput.fromJSON(object.input) : undefined,
      output: isSet(object.output) ? ScvOutput.fromJSON(object.output) : undefined,
    };
  },

  toJSON(message: ScvChannelEffectiveConfig): unknown {
    const obj: any = {};
    if (message.implementId !== undefined) {
      obj.implementId = message.implementId;
    }
    if (message.scvConnectionId !== undefined) {
      obj.scvConnectionId = message.scvConnectionId;
    }
    if (message.input !== undefined) {
      obj.input = ScvInput.toJSON(message.input);
    }
    if (message.output !== undefined) {
      obj.output = ScvOutput.toJSON(message.output);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ScvChannelEffectiveConfig>, I>>(base?: I): ScvChannelEffectiveConfig {
    return ScvChannelEffectiveConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ScvChannelEffectiveConfig>, I>>(object: I): ScvChannelEffectiveConfig {
    const message = createBaseScvChannelEffectiveConfig();
    message.implementId = object.implementId ?? undefined;
    message.scvConnectionId = object.scvConnectionId ?? undefined;
    message.input = (object.input !== undefined && object.input !== null)
      ? ScvInput.fromPartial(object.input)
      : undefined;
    message.output = (object.output !== undefined && object.output !== null)
      ? ScvOutput.fromPartial(object.output)
      : undefined;
    return message;
  },
};

function createBaseTractorEffectiveConfig(): TractorEffectiveConfig {
  return {
    tractorId: "",
    dataModelVersion: 0,
    snapshotAt: undefined,
    make: "",
    model: "",
    series: undefined,
    customerProvidedName: "",
    steeringRange: undefined,
    minTurningRadiusM: 0,
    wheelSpacingM: 0,
    wheelbaseM: 0,
    driveType: 0,
    boundary: undefined,
    tractorArchetypeId: "",
    calibration: undefined,
  };
}

export const TractorEffectiveConfig: MessageFns<TractorEffectiveConfig> = {
  encode(message: TractorEffectiveConfig, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.tractorId !== "") {
      writer.uint32(10).string(message.tractorId);
    }
    if (message.dataModelVersion !== 0) {
      writer.uint32(120).int32(message.dataModelVersion);
    }
    if (message.snapshotAt !== undefined) {
      Timestamp.encode(toTimestamp(message.snapshotAt), writer.uint32(18).fork()).join();
    }
    if (message.make !== "") {
      writer.uint32(26).string(message.make);
    }
    if (message.model !== "") {
      writer.uint32(34).string(message.model);
    }
    if (message.series !== undefined) {
      writer.uint32(42).string(message.series);
    }
    if (message.customerProvidedName !== "") {
      writer.uint32(50).string(message.customerProvidedName);
    }
    if (message.steeringRange !== undefined) {
      SteeringRange.encode(message.steeringRange, writer.uint32(58).fork()).join();
    }
    if (message.minTurningRadiusM !== 0) {
      writer.uint32(105).double(message.minTurningRadiusM);
    }
    if (message.wheelSpacingM !== 0) {
      writer.uint32(65).double(message.wheelSpacingM);
    }
    if (message.wheelbaseM !== 0) {
      writer.uint32(73).double(message.wheelbaseM);
    }
    if (message.driveType !== 0) {
      writer.uint32(80).int32(message.driveType);
    }
    if (message.boundary !== undefined) {
      Polygon2D.encode(message.boundary, writer.uint32(90).fork()).join();
    }
    if (message.tractorArchetypeId !== "") {
      writer.uint32(98).string(message.tractorArchetypeId);
    }
    if (message.calibration !== undefined) {
      TractorCalibration.encode(message.calibration, writer.uint32(114).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): TractorEffectiveConfig {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseTractorEffectiveConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.tractorId = reader.string();
          continue;
        }
        case 15: {
          if (tag !== 120) {
            break;
          }

          message.dataModelVersion = reader.int32() as any;
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.snapshotAt = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.make = reader.string();
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.model = reader.string();
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.series = reader.string();
          continue;
        }
        case 6: {
          if (tag !== 50) {
            break;
          }

          message.customerProvidedName = reader.string();
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.steeringRange = SteeringRange.decode(reader, reader.uint32());
          continue;
        }
        case 13: {
          if (tag !== 105) {
            break;
          }

          message.minTurningRadiusM = reader.double();
          continue;
        }
        case 8: {
          if (tag !== 65) {
            break;
          }

          message.wheelSpacingM = reader.double();
          continue;
        }
        case 9: {
          if (tag !== 73) {
            break;
          }

          message.wheelbaseM = reader.double();
          continue;
        }
        case 10: {
          if (tag !== 80) {
            break;
          }

          message.driveType = reader.int32() as any;
          continue;
        }
        case 11: {
          if (tag !== 90) {
            break;
          }

          message.boundary = Polygon2D.decode(reader, reader.uint32());
          continue;
        }
        case 12: {
          if (tag !== 98) {
            break;
          }

          message.tractorArchetypeId = reader.string();
          continue;
        }
        case 14: {
          if (tag !== 114) {
            break;
          }

          message.calibration = TractorCalibration.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): TractorEffectiveConfig {
    return {
      tractorId: isSet(object.tractorId)
        ? globalThis.String(object.tractorId)
        : isSet(object.tractor_id)
        ? globalThis.String(object.tractor_id)
        : "",
      dataModelVersion: isSet(object.dataModelVersion)
        ? dataModelVersionFromJSON(object.dataModelVersion)
        : isSet(object.data_model_version)
        ? dataModelVersionFromJSON(object.data_model_version)
        : 0,
      snapshotAt: isSet(object.snapshotAt)
        ? globalThis.String(object.snapshotAt)
        : isSet(object.snapshot_at)
        ? globalThis.String(object.snapshot_at)
        : undefined,
      make: isSet(object.make) ? globalThis.String(object.make) : "",
      model: isSet(object.model) ? globalThis.String(object.model) : "",
      series: isSet(object.series) ? globalThis.String(object.series) : undefined,
      customerProvidedName: isSet(object.customerProvidedName)
        ? globalThis.String(object.customerProvidedName)
        : isSet(object.customer_provided_name)
        ? globalThis.String(object.customer_provided_name)
        : "",
      steeringRange: isSet(object.steeringRange)
        ? SteeringRange.fromJSON(object.steeringRange)
        : isSet(object.steering_range)
        ? SteeringRange.fromJSON(object.steering_range)
        : undefined,
      minTurningRadiusM: isSet(object.minTurningRadiusM)
        ? globalThis.Number(object.minTurningRadiusM)
        : isSet(object.min_turning_radius_m)
        ? globalThis.Number(object.min_turning_radius_m)
        : 0,
      wheelSpacingM: isSet(object.wheelSpacingM)
        ? globalThis.Number(object.wheelSpacingM)
        : isSet(object.wheel_spacing_m)
        ? globalThis.Number(object.wheel_spacing_m)
        : 0,
      wheelbaseM: isSet(object.wheelbaseM)
        ? globalThis.Number(object.wheelbaseM)
        : isSet(object.wheelbase_m)
        ? globalThis.Number(object.wheelbase_m)
        : 0,
      driveType: isSet(object.driveType)
        ? driveTypeFromJSON(object.driveType)
        : isSet(object.drive_type)
        ? driveTypeFromJSON(object.drive_type)
        : 0,
      boundary: isSet(object.boundary) ? Polygon2D.fromJSON(object.boundary) : undefined,
      tractorArchetypeId: isSet(object.tractorArchetypeId)
        ? globalThis.String(object.tractorArchetypeId)
        : isSet(object.tractor_archetype_id)
        ? globalThis.String(object.tractor_archetype_id)
        : "",
      calibration: isSet(object.calibration) ? TractorCalibration.fromJSON(object.calibration) : undefined,
    };
  },

  toJSON(message: TractorEffectiveConfig): unknown {
    const obj: any = {};
    if (message.tractorId !== "") {
      obj.tractorId = message.tractorId;
    }
    if (message.dataModelVersion !== 0) {
      obj.dataModelVersion = dataModelVersionToJSON(message.dataModelVersion);
    }
    if (message.snapshotAt !== undefined) {
      obj.snapshotAt = message.snapshotAt;
    }
    if (message.make !== "") {
      obj.make = message.make;
    }
    if (message.model !== "") {
      obj.model = message.model;
    }
    if (message.series !== undefined) {
      obj.series = message.series;
    }
    if (message.customerProvidedName !== "") {
      obj.customerProvidedName = message.customerProvidedName;
    }
    if (message.steeringRange !== undefined) {
      obj.steeringRange = SteeringRange.toJSON(message.steeringRange);
    }
    if (message.minTurningRadiusM !== 0) {
      obj.minTurningRadiusM = message.minTurningRadiusM;
    }
    if (message.wheelSpacingM !== 0) {
      obj.wheelSpacingM = message.wheelSpacingM;
    }
    if (message.wheelbaseM !== 0) {
      obj.wheelbaseM = message.wheelbaseM;
    }
    if (message.driveType !== 0) {
      obj.driveType = driveTypeToJSON(message.driveType);
    }
    if (message.boundary !== undefined) {
      obj.boundary = Polygon2D.toJSON(message.boundary);
    }
    if (message.tractorArchetypeId !== "") {
      obj.tractorArchetypeId = message.tractorArchetypeId;
    }
    if (message.calibration !== undefined) {
      obj.calibration = TractorCalibration.toJSON(message.calibration);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<TractorEffectiveConfig>, I>>(base?: I): TractorEffectiveConfig {
    return TractorEffectiveConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<TractorEffectiveConfig>, I>>(object: I): TractorEffectiveConfig {
    const message = createBaseTractorEffectiveConfig();
    message.tractorId = object.tractorId ?? "";
    message.dataModelVersion = object.dataModelVersion ?? 0;
    message.snapshotAt = object.snapshotAt ?? undefined;
    message.make = object.make ?? "";
    message.model = object.model ?? "";
    message.series = object.series ?? undefined;
    message.customerProvidedName = object.customerProvidedName ?? "";
    message.steeringRange = (object.steeringRange !== undefined && object.steeringRange !== null)
      ? SteeringRange.fromPartial(object.steeringRange)
      : undefined;
    message.minTurningRadiusM = object.minTurningRadiusM ?? 0;
    message.wheelSpacingM = object.wheelSpacingM ?? 0;
    message.wheelbaseM = object.wheelbaseM ?? 0;
    message.driveType = object.driveType ?? 0;
    message.boundary = (object.boundary !== undefined && object.boundary !== null)
      ? Polygon2D.fromPartial(object.boundary)
      : undefined;
    message.tractorArchetypeId = object.tractorArchetypeId ?? "";
    message.calibration = (object.calibration !== undefined && object.calibration !== null)
      ? TractorCalibration.fromPartial(object.calibration)
      : undefined;
    return message;
  },
};

function createBaseImplementEffectiveConfig(): ImplementEffectiveConfig {
  return {
    implementId: "",
    dataModelVersion: 0,
    implementArchetypeId: undefined,
    snapshotAt: undefined,
    make: "",
    model: "",
    series: undefined,
    customerProvidedName: "",
    activeHitch: undefined,
    atim: undefined,
    workingWidthM: 0,
    workingWidthMLeft: 0,
    workingWidthMRight: 0,
    workingStartOffsetM: 0,
    workingEndOffsetM: 0,
    turnDirection: undefined,
    toolSpeedMS: 0,
    transitionSpeedMS: 0,
    runUpDistanceM: 0,
    straightenDistanceM: 0,
    turningRadiusLimits: [],
    boundary: undefined,
    calibration: undefined,
  };
}

export const ImplementEffectiveConfig: MessageFns<ImplementEffectiveConfig> = {
  encode(message: ImplementEffectiveConfig, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
    if (message.implementId !== "") {
      writer.uint32(10).string(message.implementId);
    }
    if (message.dataModelVersion !== 0) {
      writer.uint32(160).int32(message.dataModelVersion);
    }
    if (message.implementArchetypeId !== undefined) {
      writer.uint32(138).string(message.implementArchetypeId);
    }
    if (message.snapshotAt !== undefined) {
      Timestamp.encode(toTimestamp(message.snapshotAt), writer.uint32(18).fork()).join();
    }
    if (message.make !== "") {
      writer.uint32(26).string(message.make);
    }
    if (message.model !== "") {
      writer.uint32(34).string(message.model);
    }
    if (message.series !== undefined) {
      writer.uint32(42).string(message.series);
    }
    if (message.customerProvidedName !== "") {
      writer.uint32(50).string(message.customerProvidedName);
    }
    if (message.activeHitch !== undefined) {
      TractorHitch.encode(message.activeHitch, writer.uint32(58).fork()).join();
    }
    if (message.atim !== undefined) {
      writer.uint32(64).int32(message.atim);
    }
    if (message.workingWidthM !== 0) {
      writer.uint32(73).double(message.workingWidthM);
    }
    if (message.workingWidthMLeft !== 0) {
      writer.uint32(169).double(message.workingWidthMLeft);
    }
    if (message.workingWidthMRight !== 0) {
      writer.uint32(177).double(message.workingWidthMRight);
    }
    if (message.workingStartOffsetM !== 0) {
      writer.uint32(81).double(message.workingStartOffsetM);
    }
    if (message.workingEndOffsetM !== 0) {
      writer.uint32(89).double(message.workingEndOffsetM);
    }
    if (message.turnDirection !== undefined) {
      writer.uint32(184).int32(message.turnDirection);
    }
    if (message.toolSpeedMS !== 0) {
      writer.uint32(97).double(message.toolSpeedMS);
    }
    if (message.transitionSpeedMS !== 0) {
      writer.uint32(105).double(message.transitionSpeedMS);
    }
    if (message.runUpDistanceM !== 0) {
      writer.uint32(113).double(message.runUpDistanceM);
    }
    if (message.straightenDistanceM !== 0) {
      writer.uint32(121).double(message.straightenDistanceM);
    }
    for (const v of message.turningRadiusLimits) {
      ImplementHeightTurningRadiusLimit.encode(v!, writer.uint32(146).fork()).join();
    }
    if (message.boundary !== undefined) {
      Polygon2D.encode(message.boundary, writer.uint32(130).fork()).join();
    }
    if (message.calibration !== undefined) {
      ImplementCalibration.encode(message.calibration, writer.uint32(154).fork()).join();
    }
    return writer;
  },

  decode(input: BinaryReader | Uint8Array, length?: number): ImplementEffectiveConfig {
    const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
    const end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseImplementEffectiveConfig();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1: {
          if (tag !== 10) {
            break;
          }

          message.implementId = reader.string();
          continue;
        }
        case 20: {
          if (tag !== 160) {
            break;
          }

          message.dataModelVersion = reader.int32() as any;
          continue;
        }
        case 17: {
          if (tag !== 138) {
            break;
          }

          message.implementArchetypeId = reader.string();
          continue;
        }
        case 2: {
          if (tag !== 18) {
            break;
          }

          message.snapshotAt = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        }
        case 3: {
          if (tag !== 26) {
            break;
          }

          message.make = reader.string();
          continue;
        }
        case 4: {
          if (tag !== 34) {
            break;
          }

          message.model = reader.string();
          continue;
        }
        case 5: {
          if (tag !== 42) {
            break;
          }

          message.series = reader.string();
          continue;
        }
        case 6: {
          if (tag !== 50) {
            break;
          }

          message.customerProvidedName = reader.string();
          continue;
        }
        case 7: {
          if (tag !== 58) {
            break;
          }

          message.activeHitch = TractorHitch.decode(reader, reader.uint32());
          continue;
        }
        case 8: {
          if (tag !== 64) {
            break;
          }

          message.atim = reader.int32() as any;
          continue;
        }
        case 9: {
          if (tag !== 73) {
            break;
          }

          message.workingWidthM = reader.double();
          continue;
        }
        case 21: {
          if (tag !== 169) {
            break;
          }

          message.workingWidthMLeft = reader.double();
          continue;
        }
        case 22: {
          if (tag !== 177) {
            break;
          }

          message.workingWidthMRight = reader.double();
          continue;
        }
        case 10: {
          if (tag !== 81) {
            break;
          }

          message.workingStartOffsetM = reader.double();
          continue;
        }
        case 11: {
          if (tag !== 89) {
            break;
          }

          message.workingEndOffsetM = reader.double();
          continue;
        }
        case 23: {
          if (tag !== 184) {
            break;
          }

          message.turnDirection = reader.int32() as any;
          continue;
        }
        case 12: {
          if (tag !== 97) {
            break;
          }

          message.toolSpeedMS = reader.double();
          continue;
        }
        case 13: {
          if (tag !== 105) {
            break;
          }

          message.transitionSpeedMS = reader.double();
          continue;
        }
        case 14: {
          if (tag !== 113) {
            break;
          }

          message.runUpDistanceM = reader.double();
          continue;
        }
        case 15: {
          if (tag !== 121) {
            break;
          }

          message.straightenDistanceM = reader.double();
          continue;
        }
        case 18: {
          if (tag !== 146) {
            break;
          }

          message.turningRadiusLimits.push(ImplementHeightTurningRadiusLimit.decode(reader, reader.uint32()));
          continue;
        }
        case 16: {
          if (tag !== 130) {
            break;
          }

          message.boundary = Polygon2D.decode(reader, reader.uint32());
          continue;
        }
        case 19: {
          if (tag !== 154) {
            break;
          }

          message.calibration = ImplementCalibration.decode(reader, reader.uint32());
          continue;
        }
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skip(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ImplementEffectiveConfig {
    return {
      implementId: isSet(object.implementId)
        ? globalThis.String(object.implementId)
        : isSet(object.implement_id)
        ? globalThis.String(object.implement_id)
        : "",
      dataModelVersion: isSet(object.dataModelVersion)
        ? dataModelVersionFromJSON(object.dataModelVersion)
        : isSet(object.data_model_version)
        ? dataModelVersionFromJSON(object.data_model_version)
        : 0,
      implementArchetypeId: isSet(object.implementArchetypeId)
        ? globalThis.String(object.implementArchetypeId)
        : isSet(object.implement_archetype_id)
        ? globalThis.String(object.implement_archetype_id)
        : undefined,
      snapshotAt: isSet(object.snapshotAt)
        ? globalThis.String(object.snapshotAt)
        : isSet(object.snapshot_at)
        ? globalThis.String(object.snapshot_at)
        : undefined,
      make: isSet(object.make) ? globalThis.String(object.make) : "",
      model: isSet(object.model) ? globalThis.String(object.model) : "",
      series: isSet(object.series) ? globalThis.String(object.series) : undefined,
      customerProvidedName: isSet(object.customerProvidedName)
        ? globalThis.String(object.customerProvidedName)
        : isSet(object.customer_provided_name)
        ? globalThis.String(object.customer_provided_name)
        : "",
      activeHitch: isSet(object.activeHitch)
        ? TractorHitch.fromJSON(object.activeHitch)
        : isSet(object.active_hitch)
        ? TractorHitch.fromJSON(object.active_hitch)
        : undefined,
      atim: isSet(object.atim) ? atimInterfaceFromJSON(object.atim) : undefined,
      workingWidthM: isSet(object.workingWidthM)
        ? globalThis.Number(object.workingWidthM)
        : isSet(object.working_width_m)
        ? globalThis.Number(object.working_width_m)
        : 0,
      workingWidthMLeft: isSet(object.workingWidthMLeft)
        ? globalThis.Number(object.workingWidthMLeft)
        : isSet(object.working_width_m_left)
        ? globalThis.Number(object.working_width_m_left)
        : 0,
      workingWidthMRight: isSet(object.workingWidthMRight)
        ? globalThis.Number(object.workingWidthMRight)
        : isSet(object.working_width_m_right)
        ? globalThis.Number(object.working_width_m_right)
        : 0,
      workingStartOffsetM: isSet(object.workingStartOffsetM)
        ? globalThis.Number(object.workingStartOffsetM)
        : isSet(object.working_start_offset_m)
        ? globalThis.Number(object.working_start_offset_m)
        : 0,
      workingEndOffsetM: isSet(object.workingEndOffsetM)
        ? globalThis.Number(object.workingEndOffsetM)
        : isSet(object.working_end_offset_m)
        ? globalThis.Number(object.working_end_offset_m)
        : 0,
      turnDirection: isSet(object.turnDirection)
        ? turnDirectionFromJSON(object.turnDirection)
        : isSet(object.turn_direction)
        ? turnDirectionFromJSON(object.turn_direction)
        : undefined,
      toolSpeedMS: isSet(object.toolSpeedMS)
        ? globalThis.Number(object.toolSpeedMS)
        : isSet(object.tool_speed_m_s)
        ? globalThis.Number(object.tool_speed_m_s)
        : 0,
      transitionSpeedMS: isSet(object.transitionSpeedMS)
        ? globalThis.Number(object.transitionSpeedMS)
        : isSet(object.transition_speed_m_s)
        ? globalThis.Number(object.transition_speed_m_s)
        : 0,
      runUpDistanceM: isSet(object.runUpDistanceM)
        ? globalThis.Number(object.runUpDistanceM)
        : isSet(object.run_up_distance_m)
        ? globalThis.Number(object.run_up_distance_m)
        : 0,
      straightenDistanceM: isSet(object.straightenDistanceM)
        ? globalThis.Number(object.straightenDistanceM)
        : isSet(object.straighten_distance_m)
        ? globalThis.Number(object.straighten_distance_m)
        : 0,
      turningRadiusLimits: globalThis.Array.isArray(object?.turningRadiusLimits)
        ? object.turningRadiusLimits.map((e: any) => ImplementHeightTurningRadiusLimit.fromJSON(e))
        : globalThis.Array.isArray(object?.turning_radius_limits)
        ? object.turning_radius_limits.map((e: any) => ImplementHeightTurningRadiusLimit.fromJSON(e))
        : [],
      boundary: isSet(object.boundary) ? Polygon2D.fromJSON(object.boundary) : undefined,
      calibration: isSet(object.calibration) ? ImplementCalibration.fromJSON(object.calibration) : undefined,
    };
  },

  toJSON(message: ImplementEffectiveConfig): unknown {
    const obj: any = {};
    if (message.implementId !== "") {
      obj.implementId = message.implementId;
    }
    if (message.dataModelVersion !== 0) {
      obj.dataModelVersion = dataModelVersionToJSON(message.dataModelVersion);
    }
    if (message.implementArchetypeId !== undefined) {
      obj.implementArchetypeId = message.implementArchetypeId;
    }
    if (message.snapshotAt !== undefined) {
      obj.snapshotAt = message.snapshotAt;
    }
    if (message.make !== "") {
      obj.make = message.make;
    }
    if (message.model !== "") {
      obj.model = message.model;
    }
    if (message.series !== undefined) {
      obj.series = message.series;
    }
    if (message.customerProvidedName !== "") {
      obj.customerProvidedName = message.customerProvidedName;
    }
    if (message.activeHitch !== undefined) {
      obj.activeHitch = TractorHitch.toJSON(message.activeHitch);
    }
    if (message.atim !== undefined) {
      obj.atim = atimInterfaceToJSON(message.atim);
    }
    if (message.workingWidthM !== 0) {
      obj.workingWidthM = message.workingWidthM;
    }
    if (message.workingWidthMLeft !== 0) {
      obj.workingWidthMLeft = message.workingWidthMLeft;
    }
    if (message.workingWidthMRight !== 0) {
      obj.workingWidthMRight = message.workingWidthMRight;
    }
    if (message.workingStartOffsetM !== 0) {
      obj.workingStartOffsetM = message.workingStartOffsetM;
    }
    if (message.workingEndOffsetM !== 0) {
      obj.workingEndOffsetM = message.workingEndOffsetM;
    }
    if (message.turnDirection !== undefined) {
      obj.turnDirection = turnDirectionToJSON(message.turnDirection);
    }
    if (message.toolSpeedMS !== 0) {
      obj.toolSpeedMS = message.toolSpeedMS;
    }
    if (message.transitionSpeedMS !== 0) {
      obj.transitionSpeedMS = message.transitionSpeedMS;
    }
    if (message.runUpDistanceM !== 0) {
      obj.runUpDistanceM = message.runUpDistanceM;
    }
    if (message.straightenDistanceM !== 0) {
      obj.straightenDistanceM = message.straightenDistanceM;
    }
    if (message.turningRadiusLimits?.length) {
      obj.turningRadiusLimits = message.turningRadiusLimits.map((e) => ImplementHeightTurningRadiusLimit.toJSON(e));
    }
    if (message.boundary !== undefined) {
      obj.boundary = Polygon2D.toJSON(message.boundary);
    }
    if (message.calibration !== undefined) {
      obj.calibration = ImplementCalibration.toJSON(message.calibration);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ImplementEffectiveConfig>, I>>(base?: I): ImplementEffectiveConfig {
    return ImplementEffectiveConfig.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ImplementEffectiveConfig>, I>>(object: I): ImplementEffectiveConfig {
    const message = createBaseImplementEffectiveConfig();
    message.implementId = object.implementId ?? "";
    message.dataModelVersion = object.dataModelVersion ?? 0;
    message.implementArchetypeId = object.implementArchetypeId ?? undefined;
    message.snapshotAt = object.snapshotAt ?? undefined;
    message.make = object.make ?? "";
    message.model = object.model ?? "";
    message.series = object.series ?? undefined;
    message.customerProvidedName = object.customerProvidedName ?? "";
    message.activeHitch = (object.activeHitch !== undefined && object.activeHitch !== null)
      ? TractorHitch.fromPartial(object.activeHitch)
      : undefined;
    message.atim = object.atim ?? undefined;
    message.workingWidthM = object.workingWidthM ?? 0;
    message.workingWidthMLeft = object.workingWidthMLeft ?? 0;
    message.workingWidthMRight = object.workingWidthMRight ?? 0;
    message.workingStartOffsetM = object.workingStartOffsetM ?? 0;
    message.workingEndOffsetM = object.workingEndOffsetM ?? 0;
    message.turnDirection = object.turnDirection ?? undefined;
    message.toolSpeedMS = object.toolSpeedMS ?? 0;
    message.transitionSpeedMS = object.transitionSpeedMS ?? 0;
    message.runUpDistanceM = object.runUpDistanceM ?? 0;
    message.straightenDistanceM = object.straightenDistanceM ?? 0;
    message.turningRadiusLimits =
      object.turningRadiusLimits?.map((e) => ImplementHeightTurningRadiusLimit.fromPartial(e)) || [];
    message.boundary = (object.boundary !== undefined && object.boundary !== null)
      ? Polygon2D.fromPartial(object.boundary)
      : undefined;
    message.calibration = (object.calibration !== undefined && object.calibration !== null)
      ? ImplementCalibration.fromPartial(object.calibration)
      : undefined;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function toTimestamp(dateStr: string): Timestamp {
  const date = new globalThis.Date(dateStr);
  const seconds = Math.trunc(date.getTime() / 1_000);
  const nanos = (date.getTime() % 1_000) * 1_000_000;
  return { seconds, nanos };
}

function fromTimestamp(t: Timestamp): string {
  let millis = (t.seconds || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new globalThis.Date(millis).toISOString();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}

export interface MessageFns<T> {
  encode(message: T, writer?: BinaryWriter): BinaryWriter;
  decode(input: BinaryReader | Uint8Array, length?: number): T;
  fromJSON(object: any): T;
  toJSON(message: T): unknown;
  create<I extends Exact<DeepPartial<T>, I>>(base?: I): T;
  fromPartial<I extends Exact<DeepPartial<T>, I>>(object: I): T;
}
